import {FormControl, FormGroup} from '@angular/forms';

import {map} from 'rxjs/operators';

import {
    FundMandatoryProductPriceTypesInterface, FundProductInterface,
    FundProductPriceModel,
    FundProductPricesEnum,
    FundProductPricesService,
    FundProductWithPricesInputModel,
    FundProductWithPricesInterface,
    FundProductWithPricesModel,
    HttpStatusCodesEnum,
    IProductPriceBase,
    ServerResponse,
    ServerResponseData,
} from '@spa/api';

import {RequiredValidatorKey} from '@spa/validators';

import {BaseControl, Helper} from 'appShared/classes';

import {AddEditProductPriceFormErrorStatesEnum} from 'appEntries/panel/enums';

import {
    AddEditProductAccruedInterestSelector,
    AddEditProductPriceAskPriceSelector,
    AddEditProductPriceBidPriceSelector,
    AddEditProductPriceDateSelector,
    AddEditProductPriceFormControls,
    AddEditProductPriceIssuePriceSelector,
    AddEditProductPriceNetAssetValueSelector,
    AddEditProductPricePenalRedemptionPriceSelector,
    AddEditProductPricesLabel,
    AddEditProductYieldToMaturitySelector,
} from 'appAssets/descriptors';

export class PanelSettingsProductsAddEditPriceForm {
    private pricesToUpdate: FundProductPriceModel[];

    public formGroup: FormGroup;
    public controls: BaseControl[];

    constructor(
        private product: FundProductInterface,
        private prices: FundProductPriceModel[],
        private fundProductPricesService: FundProductPricesService,
    ) {
    }

    private initControl(control: BaseControl): BaseControl {
        switch (control.name) {
            case AddEditProductPriceDateSelector:
                control.value = this.pricesToUpdate[0].priceDate;
                control.disabled = true;
                break;
            case AddEditProductPriceIssuePriceSelector:
                control.value = this.getPriceValueByType(FundProductPricesEnum.IssuePrice);
                break;
            case AddEditProductPriceAskPriceSelector:
                control.value = this.getPriceValueByType(FundProductPricesEnum.AskPrice);
                break;
            case AddEditProductPricePenalRedemptionPriceSelector:
                control.value = this.getPriceValueByType(FundProductPricesEnum.PenalRedemptionPrice);
                break;
            case AddEditProductPriceBidPriceSelector:
                control.value = this.getPriceValueByType(FundProductPricesEnum.BidPrice);
                break;
            case AddEditProductPriceNetAssetValueSelector:
                control.value = this.getPriceValueByType(FundProductPricesEnum.NetAssetValue);
                break;
            case AddEditProductYieldToMaturitySelector:
                control.value = this.pricesToUpdate[0].yieldToMaturity;
                break;
            case AddEditProductAccruedInterestSelector:
                control.value = this.pricesToUpdate[0].accruedInterest;
                break;
            default:
                control.value = null;
                break;
        }

        return control;
    }

    private getPriceValueByType(type: FundProductPricesEnum): null | string {
        const filteredPrice = this.pricesToUpdate.find(price => price.priceType === type);

        return !filteredPrice ? null : filteredPrice.value.toFixed(4);
    }

    private createInputModel(
        productId: number,
        user: string,
        formGroup: FormGroup,
    ): FundProductWithPricesInputModel {
        const date = formGroup.value[AddEditProductPriceDateSelector];

        const prices = Object.keys(formGroup.value)
            .filter(key => key !== AddEditProductPriceDateSelector)
            .map(key => {
                let type = null;

                switch (key) {
                    case AddEditProductPriceIssuePriceSelector:
                        type = FundProductPricesEnum.IssuePrice;
                        break;
                    case AddEditProductPriceBidPriceSelector:
                        type = FundProductPricesEnum.BidPrice;
                        break;
                    case AddEditProductPricePenalRedemptionPriceSelector:
                        type = FundProductPricesEnum.PenalRedemptionPrice;
                        break;
                    case AddEditProductPriceNetAssetValueSelector:
                        type = FundProductPricesEnum.NetAssetValue;
                        break;
                    case AddEditProductPriceAskPriceSelector:
                        type = FundProductPricesEnum.AskPrice;
                        break;
                }

                return {
                    priceType: type,
                    value: parseFloat(this.formGroup.value[key]),
                    yieldToMaturity: parseFloat(this.formGroup.value[AddEditProductYieldToMaturitySelector]),
                    accruedInterest: parseFloat(this.formGroup.value[AddEditProductAccruedInterestSelector]),
                    description: ''
                } as IProductPriceBase;
            })
            .filter(price => price.value && price.priceType);

        return new FundProductWithPricesInputModel(
            productId,
            date,
            user,
            prices
        );
    }

    private handleRequestMap(
        isRegisterRequest: boolean,
        response: ServerResponseData<FundProductWithPricesInterface>
    ): ServerResponseData<FundProductPriceModel[]> {
        if ((response.status !== HttpStatusCodesEnum.Ok) || !response.data) {
            let errorState = isRegisterRequest
                ? AddEditProductPriceFormErrorStatesEnum.RegisterProductWithPricesFailed
                : AddEditProductPriceFormErrorStatesEnum.UpdateProductWithPricesFailed;

            if (response.status === HttpStatusCodesEnum.Conflict) {
                errorState = AddEditProductPriceFormErrorStatesEnum.RegisterProductWithPricesFailedDuplicate;
            }

            return Helper.errorsHelper().createErrorResponse<FundProductWithPricesInterface>(
                response,
                errorState
            );
        }

        const addedProductWithPricesModel = new FundProductWithPricesModel(response.data as FundProductWithPricesInterface);

        this.prices = this.prices.concat(...addedProductWithPricesModel.prices);

        return Helper.createSuccessResponse<FundProductPriceModel[]>(this.prices);
    }

    private checkForUpdate(formGroup: FormGroup): boolean {
        const issuePrice = this.getPriceValueByType(FundProductPricesEnum.IssuePrice);
        const bidPrice = this.getPriceValueByType(FundProductPricesEnum.BidPrice);
        const penalRedemptionPrice = this.getPriceValueByType(FundProductPricesEnum.PenalRedemptionPrice);
        const netAssetValue = this.getPriceValueByType(FundProductPricesEnum.NetAssetValue);
        const askPrice = this.getPriceValueByType(FundProductPricesEnum.AskPrice);

        return (
            (formGroup.value[AddEditProductPriceIssuePriceSelector] !== issuePrice)
            || (formGroup.value[AddEditProductPriceAskPriceSelector] !== askPrice)
            || (formGroup.value[AddEditProductPricePenalRedemptionPriceSelector] !== penalRedemptionPrice)
            || (formGroup.value[AddEditProductPriceBidPriceSelector] !== bidPrice)
            || (formGroup.value[AddEditProductPriceNetAssetValueSelector] !== netAssetValue)
        );
    }

    initForm(mandatoryTypes: FundMandatoryProductPriceTypesInterface, priceDate?: string): void {
        this.formGroup = new FormGroup({});
        this.pricesToUpdate = [];

        if (priceDate) {
            this.pricesToUpdate = this.prices.filter((price: FundProductPriceModel) => {
                return price.priceDate === priceDate;
            });
        }

        this.controls = AddEditProductPriceFormControls(mandatoryTypes, this.product.type, this.product.category).map((control: BaseControl) => {
            if (this.pricesToUpdate.length > 0) {
                control = this.initControl(control);
            }

            this.formGroup.addControl(control.name, new FormControl(control.value, control.validators));

            return control;
        });
    }

    validateForm(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        if (this.formGroup.invalid) {
            Object.keys(this.controls)
                .filter((controlKey: string) => (this.controls[controlKey] !== null))
                .forEach((controlKey: string) => {
                    const control = this.controls[controlKey];
                    const formControl = this.formGroup.get(control.name);

                    if (formControl && formControl.invalid) {
                        const errors = Object.keys(formControl.errors).map((errorKey: string) => {
                            return messageFunction(control.placeholder)[errorKey];
                        });

                        control.setErrors(errors);
                    }
                });

            return false;
        }

        if (
            !this.formGroup.value[AddEditProductPriceIssuePriceSelector]
            && !this.formGroup.value[AddEditProductPriceAskPriceSelector]
            && !this.formGroup.value[AddEditProductPricePenalRedemptionPriceSelector]
            && !this.formGroup.value[AddEditProductPriceBidPriceSelector]
            && !this.formGroup.value[AddEditProductPriceNetAssetValueSelector]
        ) {
            const control = this.controls[this.controls.length - 1];

            control.setError(messageFunction(AddEditProductPricesLabel)[RequiredValidatorKey]);

            return false;
        }

        return true;
    }

    createRequest(
        productId: number,
        user: string,
        isRegister: boolean): ServerResponse<FundProductPriceModel[]> | null {
        if (isRegister) {
            const model = this.createInputModel(productId, user, this.formGroup);

            return this.fundProductPricesService.register(model).pipe(
                map(this.handleRequestMap.bind(this, true))
            );
        }

        if (this.checkForUpdate(this.formGroup)) {
            const model = this.createInputModel(productId, user, this.formGroup);

            return this.fundProductPricesService.update(model).pipe(
                map(response => {
                    if (response.status === HttpStatusCodesEnum.Ok) {
                        this.prices = this.prices.filter(price => {
                            return !this.pricesToUpdate.includes(price);
                        });
                    }

                    return this.handleRequestMap(false, response);
                })
            );
        }

        return null;
    }
}
